import { catchError, map, Observable, of, tap } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { IChatOption, IChatSecuritySettings, IChatState, IGetChatTokenResponse, IUmbracoApiChatResponse } from './chat.models';
import { inject, Injectable } from '@angular/core';
import { Microservices } from 'src/app/core/constants/pol.enums';
import { SUPPORT_TEAM_TYPES } from './chat.constants';

@Injectable({
  providedIn: 'root'
})
export class ChatContentService {
  private _cachedSecuritySettings: IChatSecuritySettings = null;
  private _cachedTopics: IChatOption[] = null;
  private _chatTopicsGuidId = 'afb0e948-ad66-421c-884c-8e81ec855e6d';
  private _http = inject(HttpClient);
  private _subChatTopicsGuidId = 'f4e2a4cc-5d13-4015-9ca8-90bc3e5e0473';

  getChatTopics(): Observable<IChatOption[]> {
    if (!!this._cachedTopics) {
      return of(this._cachedTopics)
    }

    return this._http.get<IUmbracoApiChatResponse>(`${Microservices.UmbracoCMS}/ContentData/${this._chatTopicsGuidId}`)
      .pipe(
        map((res) => this._mapResponseToChatTopics(res)),
        tap((res) => this._cachedTopics = res),
        catchError((err) => {
          console.error('Failed to retrieve chat topics form Umbraco.  Error: ', err);

          return of([]);
        })
      )
  }

  getChatSecuritySettings(): Observable<IChatSecuritySettings> {
    if (!!this._cachedSecuritySettings) {
      return of(this._cachedSecuritySettings);
    }

    return this._http.get<IChatSecuritySettings>(`${Microservices.ProviderOnline}/app/SecurityOptions/Chat`)
      .pipe(
        tap((res) => this._cachedSecuritySettings = res),
        catchError((err) => {
          console.error('Failed to retrieve chat security settings.  Error: ', err);

          return of({
            taxIdAndNames: []
          });
        })
      )
  }

  getChatSubTopics(): Observable<IChatOption[]> {
    return this._http.get<IUmbracoApiChatResponse>(`${Microservices.UmbracoCMS}/ContentData/${this._subChatTopicsGuidId}`)
      .pipe(
        map((res) => this._mapResponseToChatTopics(res, true)),
        catchError((err) => {
          console.error('Failed to retrieve chat topics form Umbraco.  Error: ', err);

          return of([]);
        })
      )
  }

  postChatSelectionKey(selectionKey: string): Observable<boolean> {
    return this._http.post<{ isSuccess: boolean }>(`${Microservices.Chat}/setselectedchatoption?chatOption=${selectionKey}`, {})
      .pipe(
        map((res) => res.isSuccess),
        catchError((err) => {
          console.error('Failed to retrieve chat sub-topics form Umbraco.  Error: ', err);

          return of(false);
        })
      )
  }

  getChatToken(chatState: IChatState): Observable<IGetChatTokenResponse> {
    return this._http.post<IGetChatTokenResponse>(`${Microservices.Chat}/v2/start`, chatState)
      .pipe(
        catchError((err) => {
          console.error('Failed to retrieve chat chat app guid token.  Error: ', err);

          return of(null);
        })
      );
  }

  private _mapResponseToChatTopics(response: IUmbracoApiChatResponse, isSubTopic = false): IChatOption[] {
    let count = 0;

    const mappedTopics = response.contents.map((content) => {
      const name = content.value.value1;
      const teamNames = Object.values(SUPPORT_TEAM_TYPES);
      const isTeamSelection = teamNames.some(teamName => teamName === name);

      return {
        name,
        key: content.value.key1,
        id: count++,
        isTeamSelection,
        isSubTopic
      };
    });

    return mappedTopics;
  }
}
